/**
 * @description Multi-step modal for creating a recurring payment plan.
 *
 * Steps (`STEPS`):
 * 1. Customer - search/select existing customer
 * 2. Payment Method - select stored card or add new via HPF
 * 3. Schedule - frequency, start date, optional end date / max occurrences
 * 4. Details - amount + optional Products (LYN-250) + optional Appointment
 * 5. Review - lines + named discounts
 *
 * @dependencies
 * - recurringPlanService: API client for plan creation
 * - customersService: Customer search
 * - storedCardsService: Card list + HPF vaulting
 * - employeesService: Employee list for appointment assignment
 * - discountService: evaluate context=recurring_plan isRecurring=true
 * - recurringPlanForm: local today, Charge $ copy, startSchedulePreview
 *
 * @notes Same-day first charge uses preview.isFirstChargeToday.
 *   Default start and viewerToday are localTodayYmd() so a PST merchant on an
 *   EST company still gets Charge $ / an immediate debit on their today.
 *   Preview effect invalidates immediately and drops in-flight results so a
 *   slower older response cannot keep a stale Charge $ / Create Plan label.
 */

import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronLeft, ChevronRight, Check, Calendar, CreditCard, User, Clock, FileText } from 'lucide-react';
import recurringPlanService from '../../services/api/recurringPlanService';
import discountService from '../../services/api/discountService';
import { getApiErrorMessages } from '../../utils/apiErrorMessage';
import { customersService } from '../../services/api';
import storedCardsService from '../../services/api/storedCardsService';
import employeeService from '../../services/api/employeeService';
import RecurringPlanProductsField from './RecurringPlanProductsField';
import formatStackedDiscounts from '../../utils/formatStackedDiscounts';
import { formatCurrency } from '../../utils/invoiceHelpers';
import { localTodayYmd, createPlanSubmitLabel, startSchedulePreview } from '../../utils/recurringPlanForm';

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

const STEPS = ['Customer', 'Payment', 'Schedule', 'Details', 'Review'];

export default function CreateRecurringPlanModal({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  // Step 1: Customer
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Step 2: Payment Method
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [cardsLoading, setCardsLoading] = useState(false);

  // Step 3: Schedule
  // Phase 1 (migration 179): start_date is the canonical billing cycle anchor.
  // day_of_month / day_of_week are no longer user-editable — the backend derives
  // them from start_date so the two fields can never conflict.
  const [frequency, setFrequency] = useState('monthly');
  const [intervalCount, setIntervalCount] = useState(1);
  const [startDate, setStartDate] = useState(() => localTodayYmd());
  const [endDate, setEndDate] = useState('');
  const [maxOccurrences, setMaxOccurrences] = useState('');
  const [hasEndCondition, setHasEndCondition] = useState(false);
  const [endConditionType, setEndConditionType] = useState('date'); // 'date' or 'occurrences'

  // Live schedule preview (server-computed via /recurring-plans/preview)
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Employees (for appointment assignment)
  const [employees, setEmployees] = useState([]);

  // Step 4: Details + Optional Products + Optional Appointment
  const [amountDollars, setAmountDollars] = useState('');
  const [description, setDescription] = useState('');
  const [productItems, setProductItems] = useState([]);
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [evaluatedTotalCents, setEvaluatedTotalCents] = useState(null);
  const [hasLinkedEvent, setHasLinkedEvent] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventEmployeeId, setEventEmployeeId] = useState('');
  const [eventLocationAddress, setEventLocationAddress] = useState('');
  const [eventStartTime, setEventStartTime] = useState('09:00');
  const [eventDurationMinutes, setEventDurationMinutes] = useState(60);

  // Load customers and employees on mount
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const result = await customersService.getMerchantCustomers();
        setCustomers(result.customers || result || []);
      } catch (err) {
        console.error('Failed to load customers:', err);
      }
    };
    const loadEmployees = async () => {
      try {
        const result = await employeeService.getDirectory({ status: 'active' });
        setEmployees(result.employees || result || []);
      } catch (err) {
        console.error('Failed to load employees:', err);
      }
    };
    loadCustomers();
    loadEmployees();
  }, []);

  // Load cards when customer is selected
  useEffect(() => {
    if (!selectedCustomer) {
      setCards([]);
      setSelectedCardId(null);
      return;
    }
    const loadCards = async () => {
      try {
        setCardsLoading(true);
        const result = await storedCardsService.getCustomerCards(selectedCustomer.id);
        const activeCards = (result.cards || []).filter(c => c.isActive !== false);
        setCards(activeCards);
        // Auto-select default card
        const defaultCard = activeCards.find(c => c.isDefault);
        if (defaultCard) setSelectedCardId(defaultCard.id);
        else if (activeCards.length === 1) setSelectedCardId(activeCards[0].id);
      } catch (err) {
        console.error('Failed to load cards:', err);
      } finally {
        setCardsLoading(false);
      }
    };
    loadCards();
  }, [selectedCustomer]);

  // Debounced live schedule preview. Invalidate immediately so a stale Charge $
  // label cannot linger; cleanup drops in-flight results (clearTimeout alone
  // does not ignore a slower older HTTP response).
  useEffect(() => {
    if (!startDate || !frequency) {
      setSchedulePreview(null);
      setPreviewLoading(false);
      return undefined;
    }
    setSchedulePreview(null);
    setPreviewLoading(true);
    return startSchedulePreview({
      delayMs: 250,
      fetchPreview: () => recurringPlanService.previewSchedule({
        startDate,
        frequency,
        intervalCount: parseInt(intervalCount, 10) || 1,
        endDate: hasEndCondition && endConditionType === 'date' && endDate ? endDate : undefined,
        maxOccurrences: hasEndCondition && endConditionType === 'occurrences' && maxOccurrences
          ? parseInt(maxOccurrences, 10) : undefined,
        viewerToday: localTodayYmd(),
      }),
      onResult: (result) => setSchedulePreview(result),
      onError: (err) => {
        console.error('Failed to fetch schedule preview', err);
        setSchedulePreview(null);
      },
      onSettled: () => setPreviewLoading(false),
    });
  }, [startDate, frequency, intervalCount, hasEndCondition, endConditionType, endDate, maxOccurrences]);

  useEffect(() => {
    if (productItems.length === 0) {
      setAppliedDiscounts([]);
      setEvaluatedTotalCents(null);
      return undefined;
    }
    const handle = setTimeout(async () => {
      const subtotalCents = productItems.reduce(
        (sum, item) => sum + (Number(item.unitPriceCents) || 0) * (Number(item.quantity) || 1),
        0
      );
      try {
        const result = await discountService.evaluate({
          context: 'recurring_plan',
          isRecurring: true,
          items: productItems.map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity
          })),
          subtotalCents
        });
        setAppliedDiscounts(result.appliedDiscounts || []);
        setEvaluatedTotalCents(Number(result.totalCents));
      } catch (err) {
        console.error('Failed to evaluate plan discounts', err);
        setEvaluatedTotalCents(subtotalCents);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [productItems]);

  const hasProducts = productItems.length > 0;
  const productSubtotalCents = productItems.reduce(
    (sum, item) => sum + (Number(item.unitPriceCents) || 0) * (Number(item.quantity) || 1),
    0
  );
  const displayAmountCents = hasProducts
    ? (evaluatedTotalCents != null ? evaluatedTotalCents : productSubtotalCents)
    : Math.round((parseFloat(amountDollars) || 0) * 100);

  const filteredCustomers = customerSearch
    ? customers.filter(c => {
        const searchLower = customerSearch.toLowerCase();
        return (
          (c.firstName || '').toLowerCase().includes(searchLower) ||
          (c.lastName || '').toLowerCase().includes(searchLower) ||
          (c.companyName || '').toLowerCase().includes(searchLower) ||
          (c.email || '').toLowerCase().includes(searchLower)
        );
      })
    : customers;

  const canProceed = () => {
    switch (step) {
      case 0: return !!selectedCustomer;
      case 1: return !!selectedCardId;
      case 2: return !!startDate && !!frequency;
      case 3: {
        const amountOk = hasProducts
          ? displayAmountCents > 0
          : !!amountDollars && parseFloat(amountDollars) > 0;
        const appointmentOk = !hasLinkedEvent || !!(eventTitle && eventTitle.trim());
        return amountOk && appointmentOk;
      }
      case 4: return true; // Review step
      default: return false;
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setErrorMessages([]);

      const amountCents = hasProducts
        ? displayAmountCents
        : Math.round(parseFloat(amountDollars) * 100);

      const planData = {
        customerId: selectedCustomer.id,
        storedCardId: selectedCardId,
        amountCents,
        description: hasProducts ? undefined : (description || undefined),
        items: hasProducts
          ? productItems.map((item) => ({
            productId: item.productId,
            variantId: item.variantId || undefined,
            quantity: item.quantity
          }))
          : undefined,
        frequency,
        intervalCount: parseInt(intervalCount, 10) || 1,
        // dayOfMonth / dayOfWeek are no longer client-supplied — backend derives
        // them from startDate via RecurrenceEngine.deriveAnchorMetadata.
        startDate,
        viewerToday: localTodayYmd(),
        endDate: hasEndCondition && endConditionType === 'date' && endDate ? endDate : undefined,
        maxOccurrences: hasEndCondition && endConditionType === 'occurrences' && maxOccurrences
          ? parseInt(maxOccurrences, 10) : undefined,
        hasLinkedEvent,
        eventTitle: hasLinkedEvent ? eventTitle : undefined,
        eventStartTime: hasLinkedEvent ? new Date(`${startDate}T${eventStartTime}`).toISOString() : undefined,
        eventEmployeeId: hasLinkedEvent && eventEmployeeId ? eventEmployeeId : undefined,
        eventLocationAddress: hasLinkedEvent ? eventLocationAddress : undefined,
        eventDurationMinutes: hasLinkedEvent ? parseInt(eventDurationMinutes, 10) : undefined
      };

      await recurringPlanService.createPlan(planData);
      onCreated();
    } catch (err) {
      setErrorMessages(getApiErrorMessages(err, 'Failed to create recurring plan'));
      console.error('Create plan error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatMoney = (dollars) => {
    const num = parseFloat(dollars);
    return isNaN(num) ? '$0.00' : `$${num.toFixed(2)}`;
  };

  // Render an ISO YYYY-MM-DD as a friendly local date without TZ shifting.
  // (new Date('2026-05-06') interprets as UTC midnight, which can show as May 5
  // in negative-offset timezones — splitting and constructing avoids that.)
  const formatChargeDate = (isoDate) => {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const selectedCard = cards.find(c => c.id === selectedCardId);

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">New Recurring Plan</Dialog.Title>
          <button onClick={onClose} className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex items-center px-6 pt-4 pb-2 gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                i < step ? 'bg-green-500 text-white' :
                i === step ? 'bg-brand text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs ml-1 hidden sm:inline ${i === step ? 'text-brand font-medium' : 'text-gray-400'}`}>
                {s}
              </span>
              {i < STEPS.length - 1 && <div className="flex-1 h-0.5 bg-gray-200 mx-2" />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="p-6 min-h-[300px]">
          {errorMessages.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errorMessages.length === 1 ? (
                <p>{errorMessages[0]}</p>
              ) : (
                <ul className="list-disc pl-4 space-y-1">
                  {errorMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Step 0: Customer */}
          {step === 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Select Customer
              </h4>
              <input
                type="text"
                placeholder="Search customers..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
              <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
                {filteredCustomers.slice(0, 20).map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCustomer(c)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                      selectedCustomer?.id === c.id ? 'bg-brand/5 border-l-2 border-brand' : ''
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {c.firstName} {c.lastName}
                      {c.companyName && <span className="text-gray-500 ml-1">({c.companyName})</span>}
                    </p>
                    <p className="text-xs text-gray-500">{c.email}</p>
                  </button>
                ))}
                {filteredCustomers.length === 0 && (
                  <p className="px-4 py-6 text-center text-sm text-gray-400">No customers found</p>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Payment Method */}
          {step === 1 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Select Payment Method
              </h4>
              {cardsLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-14 bg-gray-200 rounded" />
                  <div className="h-14 bg-gray-200 rounded" />
                </div>
              ) : cards.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No cards on file for this customer.</p>
                  <p className="text-sm mt-1">Add a card on the customer's detail page first.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cards.map((card) => (
                    <button
                      key={card.id}
                      onClick={() => setSelectedCardId(card.id)}
                      className={`w-full text-left px-4 py-3 border rounded-lg transition-colors ${
                        selectedCardId === card.id
                          ? 'border-brand bg-brand/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {card.cardBrand || 'Card'} ending in {card.lastFour || '****'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Expires {String(card.expiryMonth).padStart(2, '0')}/{String(card.expiryYear).slice(-2)}
                            {card.isDefault && ' · Default'}
                          </p>
                        </div>
                        {selectedCardId === card.id && (
                          <Check className="w-5 h-5 text-brand" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Schedule */}
          {step === 2 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Set Schedule
              </h4>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Frequency</label>
                    <select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                    >
                      {FREQUENCY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Every</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={intervalCount}
                        onChange={(e) => setIntervalCount(e.target.value)}
                        className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                      <span className="text-sm text-gray-500">
                        {frequency === 'weekly' ? 'week(s)' : frequency === 'monthly' ? 'month(s)' : frequency === 'quarterly' ? 'quarter(s)' : 'year(s)'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The customer will be billed on this day, then on each cycle anchor going forward.
                  </p>
                </div>

                {/* Live schedule preview — show loading after invalidate so a
                    stale list cannot linger and the box does not vanish. */}
                {(previewLoading || schedulePreview?.upcomingCharges?.length > 0) && (
                  <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-4 py-3">
                    <p className="text-xs font-medium text-indigo-900 mb-1">
                      Upcoming charges
                      {previewLoading && <span className="ml-2 text-indigo-500">updating…</span>}
                    </p>
                    {schedulePreview?.upcomingCharges?.length > 0 && (
                      <ul className="text-sm text-indigo-900 space-y-0.5">
                        {schedulePreview.upcomingCharges.slice(0, 5).map((d) => (
                          <li key={d}>{formatChargeDate(d)}</li>
                        ))}
                      </ul>
                    )}
                    {schedulePreview?.isMonthEndAnchor && (
                      <p className="text-xs text-indigo-700 mt-2">
                        Month-end anchor — this plan adjusts to the last day of each month (Feb 28/29, Apr 30, etc.).
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={hasEndCondition}
                      onChange={(e) => setHasEndCondition(e.target.checked)}
                      className="rounded border-gray-300 text-brand focus:ring-brand"
                    />
                    Set an end condition
                  </label>
                </div>

                {hasEndCondition && (
                  <div className="pl-6 space-y-3">
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="endCondition"
                          checked={endConditionType === 'date'}
                          onChange={() => setEndConditionType('date')}
                          className="text-brand focus:ring-brand"
                        />
                        End date
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="endCondition"
                          checked={endConditionType === 'occurrences'}
                          onChange={() => setEndConditionType('occurrences')}
                          className="text-brand focus:ring-brand"
                        />
                        Number of payments
                      </label>
                    </div>
                    {endConditionType === 'date' ? (
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                    ) : (
                      <input
                        type="number"
                        min="1"
                        max="999"
                        placeholder="e.g., 12"
                        value={maxOccurrences}
                        onChange={(e) => setMaxOccurrences(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                    )}
                    <p className="text-xs text-gray-400">
                      {hasEndCondition
                        ? 'This plan will use "subscription" credential type (finite agreement).'
                        : 'Without an end condition, this is a "recurring" standing order.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Amount + Description + Optional Appointment */}
          {step === 3 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Plan Details
              </h4>
              <div className="space-y-4">
                <RecurringPlanProductsField items={productItems} onChange={setProductItems} />
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Amount ($)</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    value={hasProducts ? (displayAmountCents / 100).toFixed(2) : amountDollars}
                    onChange={(e) => setAmountDollars(e.target.value)}
                    readOnly={hasProducts}
                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand ${hasProducts ? 'bg-gray-50 text-gray-700' : ''}`}
                  />
                  {hasProducts && (
                    <p className="text-xs text-gray-400 mt-1">
                      Amount is list prices minus named discounts.
                    </p>
                  )}
                </div>
                {!hasProducts && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Description (shown on invoice)</label>
                    <input
                      type="text"
                      placeholder="e.g., Monthly Hot Tub Service"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                    />
                  </div>
                )}

                {/* Optional Appointment */}
                <div className="border-t pt-4">
                  <label className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <input
                      type="checkbox"
                      checked={hasLinkedEvent}
                      onChange={(e) => setHasLinkedEvent(e.target.checked)}
                      className="rounded border-gray-300 text-brand focus:ring-brand"
                    />
                    <Clock className="w-4 h-4" />
                    Create a linked appointment
                  </label>

                  {hasLinkedEvent && (
                    <div className="pl-6 space-y-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Appointment Title</label>
                        <input
                          type="text"
                          placeholder="e.g., Hot Tub Cleaning"
                          value={eventTitle}
                          onChange={(e) => setEventTitle(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                        />
                        {hasLinkedEvent && !eventTitle.trim() && (
                          <p className="text-xs text-red-600 mt-1">Appointment title is required</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Location Address</label>
                        <input
                          type="text"
                          placeholder="123 Main St, City, ST 12345"
                          value={eventLocationAddress}
                          onChange={(e) => setEventLocationAddress(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Appointment Time</label>
                        <input
                          type="time"
                          value={eventStartTime}
                          onChange={(e) => setEventStartTime(e.target.value)}
                          className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Assigned Employee</label>
                        <select
                          value={eventEmployeeId}
                          onChange={(e) => setEventEmployeeId(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                        >
                          <option value="">Select Employee (optional)</option>
                          {employees.map(emp => (
                            <option key={emp.id} value={emp.id}>
                              {emp.firstName} {emp.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Duration (minutes)</label>
                        <input
                          type="number"
                          min="15"
                          step="15"
                          value={eventDurationMinutes}
                          onChange={(e) => setEventDurationMinutes(e.target.value)}
                          className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-4">Review & Confirm</h4>
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Customer</span>
                  <span className="text-sm font-medium">
                    {selectedCustomer?.firstName} {selectedCustomer?.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Amount</span>
                  <span className="text-sm font-medium">
                    {hasProducts ? formatCurrency(displayAmountCents / 100) : formatMoney(amountDollars)}
                  </span>
                </div>
                {hasProducts && (
                  <div className="pt-2 border-t border-gray-200 space-y-1">
                    <span className="text-sm text-gray-500">Line items</span>
                    {productItems.map((item, index) => (
                      <div key={`${item.productId}-${index}`} className="flex justify-between text-sm">
                        <span className="text-gray-700">
                          {item.itemName} × {item.quantity}
                        </span>
                        <span className="font-medium">
                          {formatCurrency(((item.unitPriceCents || 0) * (item.quantity || 1)) / 100)}
                        </span>
                      </div>
                    ))}
                    {appliedDiscounts.length > 0 && (
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-gray-500">Discounts</span>
                        <span className="font-medium">{formatStackedDiscounts(appliedDiscounts)}</span>
                      </div>
                    )}
                    {appliedDiscounts.map((row) => (
                      <div key={row.discountId || row.title} className="flex justify-between text-xs text-gray-500">
                        <span>{row.title}</span>
                        <span>-{formatCurrency((Number(row.amountCents) || 0) / 100)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Frequency</span>
                  <span className="text-sm font-medium">
                    {intervalCount > 1 ? `Every ${intervalCount} ` : ''}
                    {FREQUENCY_OPTIONS.find(f => f.value === frequency)?.label || frequency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Start Date</span>
                  <span className="text-sm font-medium">
                    {schedulePreview?.firstChargeDate
                      ? formatChargeDate(schedulePreview.firstChargeDate)
                      : startDate}
                  </span>
                </div>
                {schedulePreview?.upcomingCharges?.length > 1 && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-sm text-gray-500">Upcoming charges</span>
                    <ul className="text-sm font-medium text-gray-800 mt-1 space-y-0.5">
                      {schedulePreview.upcomingCharges.slice(0, 4).map((d) => (
                        <li key={d}>{formatChargeDate(d)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedCard && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Payment Method</span>
                    <span className="text-sm font-medium">
                      {selectedCard.cardBrand} ****{selectedCard.lastFour}
                    </span>
                  </div>
                )}
                {!hasProducts && description && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Description</span>
                    <span className="text-sm font-medium">{description}</span>
                  </div>
                )}
                {hasEndCondition && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">End Condition</span>
                    <span className="text-sm font-medium">
                      {endConditionType === 'date' ? `Ends ${endDate}` : `${maxOccurrences} payments`}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Type</span>
                  <span className="text-sm font-medium">
                    {hasEndCondition ? 'Subscription (finite)' : 'Recurring (ongoing)'}
                  </span>
                </div>
                {hasLinkedEvent && eventTitle && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Linked Appointment</span>
                    <span className="text-sm font-medium">{eventTitle}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                By creating this plan, you confirm the customer has authorized recurring charges to this card,
                including any credit-card processing surcharge that applies at the time of each charge.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={step === 0 ? onClose : () => setStep(s => s - 1)}
            className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-1 px-6 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || previewLoading}
              className="flex items-center gap-1 px-6 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover disabled:opacity-50"
            >
              {createPlanSubmitLabel({
                submitting,
                isFirstChargeToday: !!schedulePreview?.isFirstChargeToday,
                amountCents: displayAmountCents
              })}
              {!submitting && <Check className="w-4 h-4" />}
            </button>
          )}
        </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
